import React from 'react'
export default function LoginModal({ isOpen, onClose, onLogin, mode }) {
    const [currentMode, setCurrentMode] = React.useState('login'); // 'login', 'signup', 'forgot', 'signup_choice', 'seller_signup'
    const [formData, setFormData] = React.useState({
        email: '',
        password: '',
        name: '',
        role: 'buyer',
    });
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [successMessage, setSuccessMessage] = React.useState(null);
    const [showPassword, setShowPassword] = React.useState(false);
    const [showSignupChoice, setShowSignupChoice] = React.useState(false);
        const passwordInputRef = React.useRef(null);

    // Mobile-friendly password toggle handler
    const handlePasswordToggle = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const newState = !showPassword;
        console.log('Password visibility toggle:', newState);
        setShowPassword(newState);
        
        // Force focus back to input on mobile
        setTimeout(() => {
            if (passwordInputRef.current) {
                passwordInputRef.current.focus();
            }
        }, 0);
    }, [showPassword]);

    // This effect runs when the modal is opened or the mode changes.
    // It ensures the modal shows the correct view (login or signup) initially.
    React.useEffect(() => {
        if (isOpen) {
            setCurrentMode(mode || 'login');
            // Only reset signup states if not in signup flow
            if (mode !== 'signup' || !showSignupChoice) {
                setShowSignupChoice(false);
            }
        }
    }, [isOpen, mode]);

    // Handle custom events for signup flow
    React.useEffect(() => {
        const handleShowSignupModal = (event) => {
            console.log('LoginModal: handleShowSignupModal called', event.detail);
            const { sellerFlow } = event.detail || {};
            console.log('LoginModal: sellerFlow =', sellerFlow);
            if (sellerFlow) {
                console.log('LoginModal: Setting showSignupChoice to true');
                setShowSignupChoice(true);
                // Set current mode to signup immediately
                setCurrentMode('signup');
            } else {
                console.log('LoginModal: Setting currentMode to signup');
                setCurrentMode('signup');
            }
        };

        window.addEventListener('showSignupModal', handleShowSignupModal);
        return () => window.removeEventListener('showSignupModal', handleShowSignupModal);
    }, [onLogin]);

    // Handle custom events for signup flow

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);
        setIsLoading(true);
        
        if (currentMode === 'signup') {
            try {
                const response = await fetch('/api/register.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                });

                // Check if response is JSON before parsing
                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    throw new Error('Server returned non-JSON response. Please try again.');
                }

                const result = await response.json();

                if (!response.ok) {
                    throw new Error(result.message || 'An unknown error occurred during registration.');
                }
                
                if (result.user) { // Buyer was registered and user data returned
                    setSuccessMessage("Registration successful! Logging you in...");
                    setTimeout(() => onLogin(result.user), 1500); // Log in after a short delay
                } else { // Seller was registered and a message was returned
                    setSuccessMessage(result.message);
                    // Don't auto-switch to login, let them see the message.
                    // They can switch manually when ready.
                }

            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        } else if (currentMode === 'forgot') {
            try {
                const response = await fetch('/api/forgot_password.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: formData.email }),
                });
                // Check if response is JSON before parsing
                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    throw new Error('Server returned non-JSON response. Please try again.');
                }

                const result = await response.json();
                if (!response.ok) {
                    throw new Error(result.message || 'An error occurred.');
                }
                setSuccessMessage(result.message);
                // Don't switch mode, let the user see the success message.
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        }
        else { // Login mode
            try {
                const response = await fetch('/api/login.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                });

                // Check if response is JSON before parsing
                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    throw new Error('Server returned non-JSON response. Please try again.');
                }

                const result = await response.json();

                if (!response.ok) {
                    throw new Error(result.message || 'Invalid email or password.');
                }

                // On successful login, pass the user data to the parent component
                onLogin(result.user);
                onClose(); // Close the modal

            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const resetForm = () => {
        setFormData({ email: '', password: '', name: '', role: 'buyer' });
        setError(null);
        setSuccessMessage(null);
    };

    const switchMode = (newMode) => {
        if (newMode === 'signup') {
            setShowSignupChoice(true);
        } else {
            setCurrentMode(newMode);
            setShowSignupChoice(false);
            resetForm();
        }
    };

    const handleSignupTypeSelect = (type) => {
        if (type === 'seller') {
            setCurrentMode('signup');
            setShowSignupChoice(false);
            setFormData(prev => ({ ...prev, role: 'seller' }));
        } else {
            setCurrentMode('signup');
            setShowSignupChoice(false);
            setFormData(prev => ({ ...prev, role: 'buyer' }));
        }
    };

    
    const handleSwitchToBuyer = () => {
        setCurrentMode('signup');
        setFormData(prev => ({ ...prev, role: 'buyer' }));
    };

    // Show SignupChoiceModal if needed
    console.log('LoginModal: showSignupChoice =', showSignupChoice);
    if (showSignupChoice) {
        console.log('LoginModal: Rendering SignupChoiceModal');
        return (
            <SignupChoiceModal
                isOpen={showSignupChoice}
                onClose={() => {
                    setShowSignupChoice(false);
                    onClose();
                }}
                onSelectType={handleSignupTypeSelect}
            />
        );
    }

    // Show basic login/signup form
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" onClick={onClose}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl animate-fade-in" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                            {currentMode === 'signup' && 'Sign Up'}
                            {currentMode === 'login' && 'Login'}
                            {currentMode === 'forgot' && 'Reset Password'}
                        </h2>
                        {currentMode === 'signup' && (
                            <p className="text-sm text-gray-600 mt-1">
                                Signing up as: <span className="font-semibold text-blue-600">
                                    {formData.role === 'seller' ? 'Seller' : 'Buyer'}
                                </span>
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </div>
                
                {/* --- NEW: Error and Success Message Display --- */}
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                        <span className="block sm:inline">{error}</span>
                    </div>
                )}
                {successMessage && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
                        <span className="block sm:inline">{successMessage}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {currentMode === 'signup' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                            <input
                                type="text"
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="Your full name"
                            />
                        </div>
                    )}
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="your@email.com"
                        />
                    </div>
                    
                    {currentMode !== 'forgot' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                            <div className="relative">
                                <input
                                    ref={passwordInputRef}
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={formData.password}
                                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-10"
                                    placeholder="••••••"
                                    data-type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onTouchStart={handlePasswordToggle}
                                    onMouseDown={handlePasswordToggle}
                                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700 touch-none"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                            </div>
                        </div>
                    )}
                    
                    
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full primary-gradient text-white py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading && 'Processing...'}
                        {!isLoading && currentMode === 'login' && 'Login'}
                        {!isLoading && currentMode === 'signup' && 'Create Account'}
                        {!isLoading && currentMode === 'forgot' && 'Send Reset Link'}
                    </button>
                </form>
                
                {/* Mode switching links */}
                {currentMode === 'login' && (
                    <div className="mt-4 text-center">
                        <button onClick={() => switchMode('forgot')} className="text-xs text-gray-500 hover:text-gray-700">
                            Forgot Password?
                        </button>
                    </div>
                )}
                
                {currentMode !== 'forgot' && (
                    <div className="mt-4 text-center">
                        <button onClick={() => switchMode(currentMode === 'login' ? 'signup' : 'login')} className="text-xs text-gray-500 hover:text-gray-700">
                            {currentMode === 'login' ? "Don't have an account? Sign Up" : 'Already have an account? Login'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
